import { useCallback, useEffect, useRef, useState } from 'react';
import { getClientSessionId } from '../lib/clientSession';

type StatsResponse = {
  online: number;
  listenedSeconds: number;
  updatedAt: number;
};

export type LiveStatsState = {
  online: number | null;
  listenedSeconds: number | null;
  connected: boolean;
};

const heartbeatInterval = 15_000;

export function useLiveStats(isPlaying: boolean): LiveStatsState {
  const [stats, setStats] = useState<LiveStatsState>({
    online: null,
    listenedSeconds: null,
    connected: false,
  });
  const sessionIdRef = useRef(getClientSessionId());
  const playingRef = useRef(isPlaying);
  const inFlightRef = useRef(false);

  const sendHeartbeat = useCallback(async (options?: { active?: boolean; playing?: boolean; keepalive?: boolean }) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const playing = options?.playing ?? playingRef.current;
      const active = options?.active ?? (document.visibilityState === 'visible' || playing);
      const response = await fetch('/api/stats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionIdRef.current, playing, active }),
        cache: 'no-store',
        keepalive: options?.keepalive,
      });
      if (!response.ok) throw new Error(`Stats request failed with ${response.status}`);
      const data = await response.json() as StatsResponse;
      if (!Number.isFinite(data.online) || !Number.isFinite(data.listenedSeconds)) throw new Error('Invalid stats response');
      setStats({
        online: Math.max(0, Math.round(data.online)),
        listenedSeconds: Math.max(0, Math.round(data.listenedSeconds)),
        connected: true,
      });
    } catch {
      setStats((previous) => ({ ...previous, connected: false }));
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    playingRef.current = isPlaying;
    void sendHeartbeat();
  }, [isPlaying, sendHeartbeat]);

  useEffect(() => {
    const timer = window.setInterval(() => void sendHeartbeat(), heartbeatInterval);
    const onVisibilityChange = () => void sendHeartbeat();
    const onPageHide = () => {
      const payload = JSON.stringify({ sessionId: sessionIdRef.current, playing: false, active: false });
      navigator.sendBeacon?.('/api/stats', new Blob([payload], { type: 'application/json' }));
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [sendHeartbeat]);

  return stats;
}

export function formatListenedHours(seconds: number | null) {
  if (seconds === null) return '—';
  const hours = seconds / 3600;
  if (hours < 1000) return hours.toFixed(1);
  return Math.round(hours).toLocaleString('en-US');
}
